import React from "react";
import PropTypes from "prop-types";
import { Box, Button, CircularProgress, Dialog } from "@mui/material";

/**
 * E5 design form modal pieces (New / Edit Zone, New Network):
 * DesignModal (centred 920 px card), ModalHeader (purple title + round ×),
 * FormPanel (bordered inner panel) and FormActions (message on the left, Save / Cancel on the right).
 */

export const designButtonSx = {
  primary: {
    height: 34,
    minWidth: 84,
    px: 2,
    borderRadius: "6px",
    backgroundColor: "#864797",
    color: "#fff",
    fontSize: 12,
    textTransform: "none",
    gap: "10px",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#7a3f8a", boxShadow: "none" },
    "&.Mui-disabled": { backgroundColor: "#864797", color: "#fff", opacity: 0.8 },
  },
  secondary: {
    height: 34,
    minWidth: 84,
    px: 2,
    borderRadius: "6px",
    backgroundColor: "#8f8f8f",
    color: "#fff",
    fontSize: 12,
    textTransform: "none",
    boxShadow: "none",
    "&:hover": { backgroundColor: "#7d7d7d", boxShadow: "none" },
    "&.Mui-disabled": { backgroundColor: "#8f8f8f", color: "#fff", opacity: 0.8 },
  },
};

/**
//    * @component  DesignModal
//    * @description centred form modal from the E5 design; backdrop click does nothing (forms ask before closing)
//    */
function DesignModal({ open, onClose, children, labelledBy }) {
  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
        if (onClose) onClose();
      }}
      aria-labelledby={labelledBy}
      scroll="body"
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 760,
          maxWidth: "calc(100% - 48px)",
          m: "90px auto 40px",
          verticalAlign: "top",
          borderRadius: "12px",
          boxShadow: "0 20px 48px rgba(16,24,40,.25)",
          p: "16px 20px 20px",
          boxSizing: "border-box",
          textAlign: "left",
          textTransform: "none",
          lineHeight: 1.4,
        },
      }}
      BackdropProps={{ sx: { backgroundColor: "rgba(32,32,32,.35)" } }}
    >
      {children}
    </Dialog>
  );
}

DesignModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  labelledBy: PropTypes.string,
};

export function ModalHeader({ title, onClose, disabled, titleId }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "14px" }}>
      <Box id={titleId} sx={{ color: "#864797", fontSize: 15 }}>
        {title}
      </Box>
      <Box
        component="button"
        type="button"
        aria-label="Close"
        disabled={disabled}
        onClick={onClose}
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          border: "none",
          backgroundColor: "#4b3558",
          color: "#fff",
          fontSize: 12,
          lineHeight: 1,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          p: 0,
        }}
      >
        ×
      </Box>
    </Box>
  );
}

ModalHeader.propTypes = {
  title: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  titleId: PropTypes.string,
};

export function FormPanel({ children }) {
  return (
    <Box sx={{ border: "1px solid #e3e3e8", borderRadius: "10px", p: "16px 20px 4px" }}>
      {children}
    </Box>
  );
}

/**
 * Save / Cancel row: optional message on the left (design), buttons on the right.
 * Save is a submit button; `saving` shows the spinner and disables both.
 */
export function FormActions({ saving, saveLabel, savingLabel, cancelLabel, onCancel, message, onSave }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "12px", mt: "16px" }}>
      {message ? (
        <Box role="alert" sx={{ mr: "auto", fontSize: 11, color: "#b42318" }}>
          {message}
        </Box>
      ) : null}
      <Button type="submit" onClick={onSave} disabled={saving} sx={designButtonSx.primary}>
        {saving && <CircularProgress size={16} thickness={5} sx={{ color: "#fff" }} />}
        {saving ? savingLabel : saveLabel}
      </Button>
      <Button type="button" onClick={onCancel} disabled={saving} sx={designButtonSx.secondary}>
        {cancelLabel}
      </Button>
    </Box>
  );
}

FormActions.propTypes = {
  saving: PropTypes.bool,
  saveLabel: PropTypes.string,
  savingLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  message: PropTypes.string,
};

FormActions.defaultProps = {
  saving: false,
  saveLabel: "Save",
  savingLabel: "Saving…",
  cancelLabel: "Cancel",
  message: "",
};

export default DesignModal;
